import { COMPANY_TZ } from "@/lib/constants";
import {
  isSupportedTimezone,
  US_TIMEZONE_OPTIONS,
} from "@marble/core/modules/timezones";
import { State, UserType } from "@prisma/client";
import { DateTime } from "luxon";

// The timezone option list and provider-timezone resolution live in
// @marble/core/modules/timezones — the slot engine anchors availability
// rules to the same zone the UI displays, so the logic must be shared.
// Import those from the core module directly; this file holds only
// app-side display and browser-sync helpers.

/**
 * Human-readable label for a stored timezone. Null (not yet known) renders as
 * "Not set"; an unrecognized zone falls back to the raw IANA name.
 */
export function getTimezoneLabel(timezone: string | null): string {
  if (!timezone) {
    return "Not set";
  }
  const match = US_TIMEZONE_OPTIONS.find((option) => option.value === timezone);
  return match?.label ?? timezone;
}

/**
 * Label for a zone offered as a client's state-of-care option. Where a state
 * has more than one (MULTI_ZONE_STATE_TIMEZONES — today only Arizona), the
 * alternate leads with the community that keeps it: "Mountain" says nothing
 * about why Arizona has two answers, and the person filling the form is
 * deciding whether this family is in the Navajo Nation, not picking a zone.
 * The IANA name follows in parentheses, matching the "Arizona
 * (America/Phoenix)" shape of the option beside it.
 *
 * Only the alternates need naming, so a second multi-zone state simply falls
 * through to the generic label until someone gives it one.
 */
export function getStateTimezoneLabel(
  state: State | null | undefined,
  timezone: string,
): string {
  if (state === State.AZ && timezone === "America/Denver") {
    return `Navajo Nation (${timezone})`;
  }
  return getTimezoneLabel(timezone);
}

/** The default zone used until a user's timezone is explicitly set. */
export const DEFAULT_USER_TIMEZONE = COMPANY_TZ;

/**
 * How long dismissing the "update your timezone?" prompt silences it for a
 * given detected zone before we re-check
 */
export const TIMEZONE_PROMPT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Whether a persisted dismissal still suppresses the timezone prompt. `raw` is
 * the stored marker — a `Date.now()` timestamp string, or null when the prompt
 * was never dismissed for this zone. A dismissal only holds for
 * `TIMEZONE_PROMPT_COOLDOWN_MS`; anything non-numeric (including legacy "1"
 * markers) is treated as expired so we re-prompt once and re-stamp a real
 * timestamp.
 */
export function isTimezonePromptDismissed(
  raw: string | null,
  now: number = Date.now(),
): boolean {
  if (raw === null) {
    return false;
  }
  const dismissedAt = Number(raw);
  if (!Number.isFinite(dismissedAt)) {
    return false;
  }
  return now - dismissedAt < TIMEZONE_PROMPT_COOLDOWN_MS;
}

/**
 * Whether each user type's `User.timezone` means "where this person is" — the
 * only reading a browser-detected zone can stand in for.
 *
 * Students and guardians are false. Their stored zone is the zone their care
 * happens in, derived from the student's state of care, and is what appointment
 * times and reminders render in; a family opening the portal from a hotel in
 * another state must not silently rewrite it.
 *
 * Exhaustive on purpose: adding a `UserType` is a type error here until someone
 * decides which of the two meanings it carries.
 */
export const IS_BROWSER_SYNCED_TIMEZONE_USER_TYPE: Record<UserType, boolean> = {
  [UserType.ADMIN]: true,
  [UserType.SCHOOL_WORKER]: true,
  [UserType.THERAPIST]: true,
  [UserType.STUDENT]: false,
  [UserType.GUARDIAN]: false,
};

/**
 * Whether the browser-timezone sync may run for this user type. An absent type
 * is excluded: not syncing leaves the stored zone alone, which is the
 * recoverable side of the mistake.
 */
export function isBrowserSyncedTimezoneUserType(
  userType: UserType | undefined,
): boolean {
  return userType != null && IS_BROWSER_SYNCED_TIMEZONE_USER_TYPE[userType];
}

/** What the browser-timezone sync should do given the stored and detected zones. */
export type TimezoneSyncAction = "none" | "set-silent" | "prompt";

/**
 * Decide how to reconcile a browser-detected zone against the one stored on the
 * user. Only supported zones (`US_TIMEZONE_OPTIONS`) ever trigger a change:
 * - stored is null and detected is supported -> silently backfill it
 * - stored is set and differs from a supported detected zone -> confirm first
 * - detected is unsupported, or already matches the stored zone -> do nothing
 */
export function resolveTimezoneSyncAction(
  stored: string | null,
  detected: string,
): TimezoneSyncAction {
  if (!isSupportedTimezone(detected)) {
    return "none";
  }
  if (stored === null) {
    return "set-silent";
  }
  if (stored === detected) {
    return "none";
  }
  return "prompt";
}

/**
 * Change a Date's timezone while keeping its wall-clock reading (luxon's
 * `keepLocalTime`): a browser-local Date whose fields read "Aug 3, 3:00 PM"
 * becomes the instant meaning 3:00 PM in `zone`.
 *
 * Use when a form labels its time inputs as being in some explicit zone
 * (e.g. a therapist's scheduling timezone) but the picker component reads
 * browser-local Dates.
 */
export function changeZoneKeepWallClock(date: Date, zone: string): Date {
  return DateTime.fromJSDate(date)
    .setZone(zone, { keepLocalTime: true })
    .toJSDate();
}
